type Application = {
  id: string;
  subjectType: "INDIVIDUAL" | "BUSINESS";
  status: string;
  email: string;
  ownerName: string;
  displayName: string;
  countryCode: string;
  emailVerified: boolean;
  providerId?: string;
  version: number;
  reviewNote?: string;
  updatedAt: string;
};

type Profile = {
  legalName: string;
  dateOfBirth?: string;
  idDocumentType?: string;
  idDocumentNumber?: string;
  registrationNumber?: string;
  registeredAddress: string;
  beneficialOwnerNames?: string[];
  documentRefs: string[];
  settlementNetwork: string;
  settlementWallet: string;
};

type Review = {
  id: string;
  reviewerEmail: string;
  decision: string;
  note: string;
  createdAt: string;
};

type Detail = { application: Application; profile: Profile; reviews: Review[] };
type API = <T>(path: string, options?: RequestInit) => Promise<T>;

type Options = {
  api: API;
  escapeHTML: (value: string) => string;
  canReview: () => boolean;
  onCountChange: (count: number) => void;
};

function element<T extends HTMLElement>(selector: string): T {
  return document.querySelector<T>(selector)!;
}

export function installProviderApplicationsView(options: Options) {
  let applications: Application[] = [];
  let selected: Detail | undefined;

  function status(message: string, error = false): void {
    const target = element<HTMLElement>("#provider-review-status");
    target.hidden = false;
    target.className = `form-status ${error ? "error" : "success"}`;
    target.textContent = message;
  }

  function renderList(): void {
    element<HTMLElement>("#provider-application-empty").hidden = applications.length !== 0;
    element("#provider-application-rows").innerHTML = applications.map(application => `
      <tr>
        <td><strong>${options.escapeHTML(application.displayName)}</strong><small>${options.escapeHTML(application.email)}</small></td>
        <td><strong>${application.subjectType === "INDIVIDUAL" ? "个人" : "企业"}</strong><small>${options.escapeHTML(application.countryCode)} · V${application.version}</small></td>
        <td><span class="status-chip ${application.status.toLowerCase()}">${options.escapeHTML(application.status)}</span></td>
        <td class="row-actions"><button type="button" data-provider-application="${options.escapeHTML(application.id)}">查看</button></td>
      </tr>`).join("");
    element("#provider-application-rows").querySelectorAll<HTMLButtonElement>("[data-provider-application]")
      .forEach(button => button.addEventListener("click", () => void loadDetail(button.dataset.providerApplication!)));
    options.onCountChange(applications.length);
  }

  function item(label: string, value: unknown): string {
    const text = Array.isArray(value) ? value.join("、") : String(value || "—");
    return `<div><dt>${options.escapeHTML(label)}</dt><dd>${options.escapeHTML(text)}</dd></div>`;
  }

  function renderDetail(): void {
    if (!selected) return;
    const { application, profile, reviews } = selected;
    element<HTMLElement>("#provider-review-empty").hidden = true;
    element<HTMLElement>("#provider-review-detail").hidden = false;
    element("#provider-application-version").textContent = `V${application.version}`;
    const directlyReviewable = application.status === "DRAFT" || application.status === "CHANGES_REQUIRED";
    const reviewHint = directlyReviewable
      ? "Demo 已暂时跳过邮箱验证和申请提交，可直接审核。"
      : application.status === "PENDING_REVIEW" ? "" : "当前状态不允许发起新的审核决定。";
    element("#provider-review-summary").innerHTML = `
      <span class="status-chip ${application.status.toLowerCase()}">${options.escapeHTML(application.status)}</span>
      <h2>${options.escapeHTML(application.displayName)}</h2>
      <p>${options.escapeHTML(application.ownerName)} · ${options.escapeHTML(application.email)} · ${options.escapeHTML(application.countryCode)}</p>
      ${application.reviewNote ? `<aside>${options.escapeHTML(application.reviewNote)}</aside>` : ""}
      ${reviewHint ? `<aside>${options.escapeHTML(reviewHint)}</aside>` : ""}`;
    element("#provider-profile").innerHTML = [
      item("主体类型", application.subjectType === "INDIVIDUAL" ? "个人" : "企业"),
      item("法定名称", profile.legalName),
      item("出生日期", profile.dateOfBirth),
      item("证件", profile.idDocumentType && profile.idDocumentNumber
        ? `${profile.idDocumentType} · ${profile.idDocumentNumber}` : "—"),
      item("企业注册号", profile.registrationNumber),
      item("注册地址", profile.registeredAddress),
      item("受益所有人", profile.beneficialOwnerNames),
      item("安全材料引用", profile.documentRefs),
      item("结算网络", profile.settlementNetwork),
      item("结算钱包", profile.settlementWallet),
    ].join("");
    element("#provider-review-history").innerHTML = reviews.length
      ? reviews.map(review => `<article><strong>${options.escapeHTML(review.decision)}</strong><span>${options.escapeHTML(review.reviewerEmail)} · ${options.escapeHTML(new Date(review.createdAt).toLocaleString("zh-CN"))}</span><p>${options.escapeHTML(review.note || "无备注")}</p></article>`).join("")
      : "<p>暂无审核记录。</p>";
    const reviewable = options.canReview() &&
      (application.status === "PENDING_REVIEW" || directlyReviewable);
    element<HTMLElement>("#provider-review-actions").hidden = !reviewable;
  }

  async function loadDetail(id: string): Promise<void> {
    try {
      selected = await options.api<Detail>(`/v1/admin/provider-applications/${id}`);
      renderDetail();
    } catch (error) {
      status(error instanceof Error ? error.message : "申请详情加载失败", true);
    }
  }

  async function load(): Promise<void> {
    try {
      const body = await options.api<{ applications: Application[] }>("/v1/admin/provider-applications");
      applications = body.applications;
      renderList();
      if (selected && applications.some(item => item.id === selected!.application.id)) {
        await loadDetail(selected.application.id);
      }
    } catch (error) {
      element("#provider-application-empty").textContent = error instanceof Error ? error.message : "申请列表加载失败";
      element<HTMLElement>("#provider-application-empty").hidden = false;
    }
  }

  async function decide(decision: string): Promise<void> {
    if (!selected) return;
    const note = element<HTMLTextAreaElement>("#provider-review-note").value.trim();
    const label = decision === "approve" ? "通过并创建 Provider 账号" :
      decision === "request-changes" ? "要求补件" : "拒绝";
    if (!window.confirm(`确认${label}？该决定会写入不可变审核记录。`)) return;
    try {
      selected = await options.api<Detail>(
        `/v1/admin/provider-applications/${selected.application.id}/${decision}`,
        {
          method: "POST",
          body: JSON.stringify({
            version: selected.application.version,
            note,
            identityVerified: element<HTMLInputElement>("#provider-identity-approved").checked,
            settlementApproved: element<HTMLInputElement>("#provider-settlement-approved").checked,
          }),
        },
      );
      renderDetail();
      status(`${label}已完成。`);
      await load();
    } catch (error) {
      status(error instanceof Error ? error.message : "审核失败", true);
    }
  }

  element("#refresh-provider-applications").addEventListener("click", () => void load());
  document.querySelectorAll<HTMLButtonElement>("[data-provider-decision]").forEach(button => {
    button.addEventListener("click", () => void decide(button.dataset.providerDecision!));
  });

  return { load, summary: () => `${applications.length} APPLICATIONS` };
}
